import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useHistory} from 'react-router-dom'
import {
  Chart,
  ChartArea,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartLegend,
  ChartSeries,
  ChartSeriesItem,
  ChartValueAxis,
  ChartValueAxisItem,
} from '@progress/kendo-react-charts'
import {PageTitle} from '../../../_metronic/layout/core'
import {useSelectedProject} from '../../modules/projectdetails/hooks/useSelectedProject'
import {DashboardPcgReport, getDashboardOverview} from './api'
import {DEFAULT_VARIATION_COLOR} from '../../modules/projectsetup/models'
import {CashflowMonthlyLine} from '../../modules/pcgreport/models'
import {
  buildContingencyErosionData,
  ContingencyErosionSeriesKey,
} from '../../modules/pcgreport/utils/contingencyErosion'
import {VariationStatus} from '../../modules/variationassessments/models'
import {DASHBOARD_REFRESH_EVENT} from '../../shared/constants/events'
import './DashboardWrapper.scss'

type SubmittedVariationItem = {
  id: string
  variationNumber: string
  ageDays: number
  amount: number
}

type StatusSummaryItem = {
  id: string
  label: string
  axisLabel: string
  count: number
  totalAmount: number
  color: string
  filterStatus?: VariationStatus
}

type ContingencySummary = {
  total: number
  committed: number
  remaining: number
  remainingPercent: number
  hasData: boolean
}

type DashboardBudgetSummary = {
  contractSum: number
  adjustedBudget: number
  committed: number
  hasPcgReport: boolean
}

const DEFAULT_BRAND_ORANGE = '#f79421'
const DEFAULT_BRAND_ORANGE_ACTIVE = '#d37d1a'
const DEFAULT_BRAND_BLUE = '#2b5672'
const brandOrange = `var(--brand-color-primary, ${DEFAULT_BRAND_ORANGE})`
const defaultContingencyErosionSeriesColors: Record<ContingencyErosionSeriesKey, string> = {
  cumulativeForecastExpenditure: '#3f7bc6',
  forecastContingencyErosion: '#8fb34d',
  actualCumulativeExpenditure: '#7d5fb2',
  actualCostToComplete: '#4fb6c2',
  forecastCostToComplete: '#c14953',
  actualContingencyCostToComplete: DEFAULT_BRAND_ORANGE,
  remainingContingency: DEFAULT_BRAND_ORANGE,
  contingencyForecastPercent: '#8fb34d',
  contingencyForecastFivePercent: DEFAULT_BRAND_BLUE,
}
const MIN_STATUS_COLOR_CONTRAST_RATIO = 2.2

const resolveCssVariableColor = (variableName: string, fallback: string) => {
  if (typeof window === 'undefined') {
    return fallback
  }

  const value = window
    .getComputedStyle(window.document.documentElement)
    .getPropertyValue(variableName)
    .trim()
  return value || fallback
}

const normalizeHexColor = (value?: string | null): string | undefined => {
  const input = (value ?? '').trim()
  if (!input.startsWith('#')) {
    return undefined
  }
  const raw = input.slice(1)
  if (!/^[a-f0-9]+$/i.test(raw)) {
    return undefined
  }
  if (raw.length === 3) {
    return `#${raw
      .split('')
      .map((char) => `${char}${char}`)
      .join('')
      .toUpperCase()}`
  }
  if (raw.length === 6) {
    return `#${raw.toUpperCase()}`
  }
  return undefined
}

const parseHexRgb = (value: string) => {
  const normalized = normalizeHexColor(value)
  if (!normalized) return null
  const hex = normalized.slice(1)
  const red = parseInt(hex.slice(0, 2), 16)
  const green = parseInt(hex.slice(2, 4), 16)
  const blue = parseInt(hex.slice(4, 6), 16)
  if (![red, green, blue].every((channel) => Number.isFinite(channel))) {
    return null
  }
  return {red, green, blue}
}

const srgbToLinear = (channel: number) => {
  const normalized = channel / 255
  if (normalized <= 0.03928) {
    return normalized / 12.92
  }
  return ((normalized + 0.055) / 1.055) ** 2.4
}

const relativeLuminance = (hexColor: string) => {
  const rgb = parseHexRgb(hexColor)
  if (!rgb) return null
  return (
    0.2126 * srgbToLinear(rgb.red) +
    0.7152 * srgbToLinear(rgb.green) +
    0.0722 * srgbToLinear(rgb.blue)
  )
}

const contrastRatio = (hexA: string, hexB: string) => {
  const lumA = relativeLuminance(hexA)
  const lumB = relativeLuminance(hexB)
  if (lumA === null || lumB === null) {
    return null
  }
  const lighter = Math.max(lumA, lumB)
  const darker = Math.min(lumA, lumB)
  return (lighter + 0.05) / (darker + 0.05)
}

const darkenHexColor = (hexColor: string, factor: number) => {
  const rgb = parseHexRgb(hexColor)
  if (!rgb) return hexColor
  const clamp = (value: number) => Math.max(0, Math.min(255, Math.round(value)))
  const toHex = (value: number) => value.toString(16).padStart(2, '0').toUpperCase()
  const red = clamp(rgb.red * factor)
  const green = clamp(rgb.green * factor)
  const blue = clamp(rgb.blue * factor)
  return `#${toHex(red)}${toHex(green)}${toHex(blue)}`
}

const ensureStatusColorVisibility = (inputColor?: string | null): string => {
  const trimmed = (inputColor ?? '').trim()
  if (!trimmed) {
    return DEFAULT_VARIATION_COLOR
  }

  const normalizedHex = normalizeHexColor(trimmed)
  if (!normalizedHex) {
    return trimmed
  }

  const initialContrast = contrastRatio(normalizedHex, '#FFFFFF')
  if (initialContrast === null || initialContrast >= MIN_STATUS_COLOR_CONTRAST_RATIO) {
    return normalizedHex
  }

  for (let factor = 0.92; factor >= 0.4; factor -= 0.08) {
    const candidate = darkenHexColor(normalizedHex, factor)
    const ratio = contrastRatio(candidate, '#FFFFFF')
    if (ratio !== null && ratio >= MIN_STATUS_COLOR_CONTRAST_RATIO) {
      return candidate
    }
  }

  return DEFAULT_BRAND_ORANGE_ACTIVE
}

const wrapCategoryAxisLabel = (label: string) => {
  const value = (label ?? '').trim()
  if (!value || value.includes('\n') || value.length <= 8) {
    return value
  }

  const firstSpaceIndex = value.indexOf(' ')
  if (firstSpaceIndex > 0 && firstSpaceIndex < value.length - 1) {
    return `${value.slice(0, firstSpaceIndex)}\n${value.slice(firstSpaceIndex + 1)}`
  }

  const firstSlashIndex = value.indexOf('/')
  if (firstSlashIndex > 0 && firstSlashIndex < value.length - 1) {
    return `${value.slice(0, firstSlashIndex + 1)}\n${value.slice(firstSlashIndex + 1)}`
  }

  return value
}

const formatCurrency = (value?: number | null) => {
  const numeric = Number(value ?? 0)
  if (!Number.isFinite(numeric)) {
    return '$0.00'
  }
  return numeric.toLocaleString('en-AU', {style: 'currency', currency: 'AUD'})
}

const toFiniteNumber = (value?: number | null) =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0

const roundCurrency = (value: number) => Number(value.toFixed(2))

const parseProjectCompletionDate = (value?: string | null): Date | null => {
  const input = (value ?? '').trim()
  if (!input) {
    return null
  }

  const dateOnlyMatch = input.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (dateOnlyMatch) {
    const year = Number(dateOnlyMatch[1])
    const month = Number(dateOnlyMatch[2]) - 1
    const day = Number(dateOnlyMatch[3])
    if (
      Number.isFinite(year) &&
      Number.isFinite(month) &&
      Number.isFinite(day) &&
      month >= 0 &&
      month < 12 &&
      day >= 1 &&
      day <= 31
    ) {
      return new Date(year, month, day)
    }
  }

  const parsed = new Date(input)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }
  return parsed
}

const formatCompletionDate = (value?: string | null) => {
  const parsed = parseProjectCompletionDate(value)
  if (!parsed) {
    return 'Not provided'
  }
  const day = String(parsed.getDate()).padStart(2, '0')
  const month = parsed.toLocaleString('en-AU', {month: 'short'}).toUpperCase()
  const year = parsed.getFullYear()
  return `${day}-${month}-${year}`
}

const monthLabels = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
]
const monthLabelToIndex = monthLabels.reduce<Record<string, number>>((acc, label, idx) => {
  acc[label] = idx
  return acc
}, {})

const toMonthLabel = (date: Date) => {
  const month = monthLabels[date.getMonth()] ?? ''
  const year = date.getFullYear().toString().slice(-2)
  return `${month}-${year}`
}

const parseMonthValue = (value?: string | null): Date | null => {
  if (!value) return null
  const trimmed = value.trim()
  if (!trimmed) return null

  const isoMonthMatch = trimmed.match(/^(\d{4})-(\d{1,2})(?:-(\d{1,2}))?$/)
  if (isoMonthMatch) {
    const [, yearStr, monthStr] = isoMonthMatch
    const year = Number(yearStr)
    const monthIndex = Number(monthStr) - 1
    if (!Number.isNaN(year) && monthIndex >= 0 && monthIndex < 12) {
      return new Date(year, monthIndex, 1)
    }
  }

  const abbrMatch = trimmed.toUpperCase().match(/^([A-Z]{3})[-\s/](\d{2,4})$/)
  if (abbrMatch) {
    const [, abbr, yearPart] = abbrMatch
    const monthIndex = monthLabelToIndex[abbr]
    if (monthIndex !== undefined) {
      const year = yearPart.length === 2 ? Number(`20${yearPart}`) : Number(yearPart)
      if (!Number.isNaN(year)) {
        return new Date(year, monthIndex, 1)
      }
    }
  }

  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.valueOf())) return null
  return new Date(parsed.getFullYear(), parsed.getMonth(), 1)
}

const formatMonthDisplay = (value?: string | null): string => {
  if (value === null || value === undefined) return ''
  const parsed = parseMonthValue(value)
  if (!parsed) return value
  return toMonthLabel(parsed)
}

const toNumberOrZero = (value?: number | string | null) => {
  if (value === null || value === undefined) {
    return 0
  }
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : 0
}

const buildCumulativeChartValues = (
  rows: CashflowMonthlyLine[],
  monthlyField: 'forecastMonthly' | 'actualMonthly',
  cumulativeField: 'forecastCumulative' | 'actualCumulative',
  includeValue?: (row: CashflowMonthlyLine) => boolean
) => {
  let runningTotal = 0

  return rows.map((row) => {
    const monthlyValue = toNumberOrZero(row[monthlyField] as number | string | null | undefined)
    const cumulativeValue = row[cumulativeField]
    const hasCumulativeValue =
      typeof cumulativeValue === 'number' && Number.isFinite(cumulativeValue)

    if (!hasCumulativeValue) {
      runningTotal += monthlyValue
      return includeValue && !includeValue(row) ? null : runningTotal
    }

    // Historical rows may contain zeroed cumulative values for padded months.
    if (cumulativeValue === 0 && runningTotal > 0 && monthlyValue === 0) {
      return includeValue && !includeValue(row) ? null : runningTotal
    }

    runningTotal = cumulativeValue
    return includeValue && !includeValue(row) ? null : runningTotal
  })
}

const hasActualMonthlyChartValue = (row: CashflowMonthlyLine) => {
  const actualMonthly = Number(row.actualMonthly ?? 0)
  return Number.isFinite(actualMonthly) && actualMonthly !== 0
}

const DEFAULT_DASHBOARD_BUDGET_SUMMARY: DashboardBudgetSummary = {
  contractSum: 0,
  adjustedBudget: 0,
  committed: 0,
  hasPcgReport: false,
}

const DEFAULT_CONTINGENCY_SUMMARY: ContingencySummary = {
  total: 0,
  committed: 0,
  remaining: 0,
  remainingPercent: 0,
  hasData: false,
}

const PCG_CHARTS_ROOT_MARGIN = '320px 0px'

const DashboardPage: FC = () => {
  const history = useHistory()
  const {projectId, projectName, projectNumber, isMock} = useSelectedProject()
  const [submittedItems, setSubmittedItems] = useState<SubmittedVariationItem[]>([])
  const [statusSummaries, setStatusSummaries] = useState<StatusSummaryItem[]>([])
  const [loadingVariations, setLoadingVariations] = useState(false)
  const [contingencySummary, setContingencySummary] = useState<ContingencySummary>(
    DEFAULT_CONTINGENCY_SUMMARY
  )
  const [projectCompletionDate, setProjectCompletionDate] = useState<string | null>(null)
  const [budgetSummary, setBudgetSummary] = useState<DashboardBudgetSummary>(
    DEFAULT_DASHBOARD_BUDGET_SUMMARY
  )
  const [latestPcgReport, setLatestPcgReport] = useState<DashboardPcgReport | null>(null)
  const [pcgChartsCanRender, setPcgChartsCanRender] = useState(false)
  const [reloadToken, setReloadToken] = useState(0)
  const requestIdRef = useRef(0)
  const pcgChartsSectionRef = useRef<HTMLDivElement | null>(null)

  const chartColors = useMemo(() => {
    const primary = resolveCssVariableColor('--brand-color-primary', DEFAULT_BRAND_ORANGE)
    const info = resolveCssVariableColor('--brand-color-info', DEFAULT_BRAND_BLUE)

    return {
      primary,
      info,
      contingencySeries: {
        ...defaultContingencyErosionSeriesColors,
        actualContingencyCostToComplete: primary,
        remainingContingency: primary,
        contingencyForecastFivePercent: info,
      },
    }
  }, [])

  const projectLabel = useMemo(
    () => projectName || projectNumber || '',
    [projectName, projectNumber]
  )

  const handleSubmittedBarClick = useCallback(() => {
    history.push('/variation-assessments?status=submitted')
  }, [history])

  const handleAllVariationsBarClick = useCallback(
    (item?: StatusSummaryItem) => {
      if (item?.filterStatus) {
        history.push(`/variation-assessments?status=${item.filterStatus}`)
        return
      }
      history.push('/variation-assessments')
    },
    [history]
  )

  useEffect(() => {
    if (!projectId || isMock) {
      setSubmittedItems([])
      setStatusSummaries([])
      setLoadingVariations(false)
      setContingencySummary(DEFAULT_CONTINGENCY_SUMMARY)
      setProjectCompletionDate(null)
      setBudgetSummary(DEFAULT_DASHBOARD_BUDGET_SUMMARY)
      setLatestPcgReport(null)
      setPcgChartsCanRender(false)
      return
    }

    const requestId = ++requestIdRef.current
    setLoadingVariations(true)
    setPcgChartsCanRender(false)

    const load = async () => {
      try {
        const overview = await getDashboardOverview(projectId)
        if (requestId !== requestIdRef.current) {
          return
        }

        setSubmittedItems(overview.submittedVariations ?? [])
        setStatusSummaries(
          (overview.variationStatusSummaries ?? []).map((summary) => ({
            ...summary,
            color: ensureStatusColorVisibility(summary.color ?? DEFAULT_VARIATION_COLOR),
          }))
        )
        setProjectCompletionDate(overview.projectCompletionDate ?? null)
        setBudgetSummary(overview.budgetSummary ?? DEFAULT_DASHBOARD_BUDGET_SUMMARY)
        setLatestPcgReport(overview.latestPcgReport ?? null)
        setContingencySummary(overview.contingencySummary ?? DEFAULT_CONTINGENCY_SUMMARY)
      } catch (error) {
        console.error('Failed to load dashboard data.', error)
        if (requestId === requestIdRef.current) {
          setSubmittedItems([])
          setStatusSummaries([])
          setProjectCompletionDate(null)
          setBudgetSummary(DEFAULT_DASHBOARD_BUDGET_SUMMARY)
          setLatestPcgReport(null)
          setContingencySummary(DEFAULT_CONTINGENCY_SUMMARY)
          setPcgChartsCanRender(false)
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoadingVariations(false)
        }
      }
    }

    void load()
  }, [projectId, isMock, reloadToken])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    const handleRefresh = () => setReloadToken((current) => current + 1)
    window.addEventListener(DASHBOARD_REFRESH_EVENT, handleRefresh)
    return () => window.removeEventListener(DASHBOARD_REFRESH_EVENT, handleRefresh)
  }, [])

  const submittedChartHeight = useMemo(
    () => Math.max(240, submittedItems.length * 48),
    [submittedItems.length]
  )
  const submittedValueAxisMax = useMemo(() => {
    const maxAge = submittedItems.reduce(
      (currentMax, item) => Math.max(currentMax, item.ageDays),
      0
    )
    const maxAmountLabelLength = submittedItems.reduce(
      (currentMax, item) => Math.max(currentMax, formatCurrency(item.amount).length),
      0
    )
    const headroom = Math.max(6, Math.ceil(maxAge * 0.35), Math.ceil(maxAmountLabelLength * 0.8))
    return Math.max(10, maxAge + headroom)
  }, [submittedItems])
  const allVariationsRequiresHorizontalScroll = useMemo(
    () => statusSummaries.length > 5,
    [statusSummaries.length]
  )
  const allVariationsChartWidth = useMemo(
    () =>
      allVariationsRequiresHorizontalScroll
        ? Math.max(560, statusSummaries.length * 110)
        : undefined,
    [allVariationsRequiresHorizontalScroll, statusSummaries.length]
  )
  const submittedCount = submittedItems.length
  const contingencyPercentLabel = useMemo(
    () => Math.round(contingencySummary.remainingPercent * 100),
    [contingencySummary.remainingPercent]
  )
  const gaugeRadius = 70
  const gaugeCircumference = useMemo(() => Math.PI * gaugeRadius, [gaugeRadius])
  const gaugeDash = useMemo(
    () => gaugeCircumference * contingencySummary.remainingPercent,
    [contingencySummary.remainingPercent, gaugeCircumference]
  )
  const gaugeGap = useMemo(() => gaugeCircumference - gaugeDash, [gaugeCircumference, gaugeDash])
  const allVariationsCount = useMemo(
    () => statusSummaries.reduce((total, item) => total + item.count, 0),
    [statusSummaries]
  )
  const projectCompletionDateLabel = useMemo(
    () => formatCompletionDate(projectCompletionDate),
    [projectCompletionDate]
  )
  const hasPcgReport = useMemo(() => Boolean(latestPcgReport), [latestPcgReport])
  const shouldRenderPcgCharts = hasPcgReport && pcgChartsCanRender
  const pcgChartsLoading = loadingVariations || (hasPcgReport && !pcgChartsCanRender)

  useEffect(() => {
    if (!hasPcgReport || loadingVariations) {
      setPcgChartsCanRender(false)
      return
    }

    const target = pcgChartsSectionRef.current
    if (!target || typeof window === 'undefined') {
      return
    }

    if (!('IntersectionObserver' in window)) {
      const timeoutId = setTimeout(() => setPcgChartsCanRender(true), 250)
      return () => clearTimeout(timeoutId)
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setPcgChartsCanRender(true)
          observer.disconnect()
        }
      },
      {rootMargin: PCG_CHARTS_ROOT_MARGIN}
    )

    observer.observe(target)
    return () => observer.disconnect()
  }, [hasPcgReport, latestPcgReport?.meta.id, loadingVariations])

  const constructionMonthlyRows = useMemo(
    () =>
      shouldRenderPcgCharts
        ? (latestPcgReport?.constructionCashflowMonthly ?? []).filter((row) => !row.isTotalRow)
        : [],
    [latestPcgReport?.constructionCashflowMonthly, shouldRenderPcgCharts]
  )
  const constructionMonthlyChartData = useMemo(() => {
    const categories = constructionMonthlyRows.map((row) => formatMonthDisplay(row.month))
    return {
      categories,
      actualMonthly: constructionMonthlyRows.map((row) =>
        hasActualMonthlyChartValue(row) ? toNumberOrZero(row.actualMonthly) : null
      ),
      forecastMonthly: constructionMonthlyRows.map((row) => row.forecastMonthly ?? 0),
      actualCumulative: buildCumulativeChartValues(
        constructionMonthlyRows,
        'actualMonthly',
        'actualCumulative',
        hasActualMonthlyChartValue
      ),
      forecastCumulative: buildCumulativeChartValues(
        constructionMonthlyRows,
        'forecastMonthly',
        'forecastCumulative'
      ),
    }
  }, [constructionMonthlyRows])
  const hasConstructionChartData = useMemo(
    () => constructionMonthlyChartData.categories.length > 0,
    [constructionMonthlyChartData.categories.length]
  )
  const contingencyErosionData = useMemo(() => {
    if (!shouldRenderPcgCharts || !latestPcgReport) {
      return null
    }

    return buildContingencyErosionData({
      budget: latestPcgReport.budget ?? [],
      budgetTransfers: latestPcgReport.budgetTransfers ?? [],
      variations: latestPcgReport.variations ?? [],
      monthly: latestPcgReport.constructionCashflowMonthly ?? [],
      contingencyHistory: latestPcgReport.contingencyHistory ?? [],
      constructionStartDate:
        latestPcgReport.constructionStartDate ?? latestPcgReport.projectStartDate,
      constructionCompletionDate:
        latestPcgReport.constructionPracticalCompletionDate ??
        latestPcgReport.programCompletionDate,
      reportDateOfIssue: latestPcgReport.meta.dateOfIssue,
    })
  }, [latestPcgReport, shouldRenderPcgCharts])
  const contingencyChartCategories = useMemo(
    () => contingencyErosionData?.months.map((month) => month.label) ?? [],
    [contingencyErosionData]
  )
  const hasContingencyChartData = useMemo(
    () => contingencyChartCategories.length > 0,
    [contingencyChartCategories.length]
  )

  if (!projectId || isMock) {
    return (
      <div className='card card-flush border-dashed shadow-sm'>
        <div className='card-body py-12 text-center text-muted'>
          Select a project from the sidebar to view dashboard data.
        </div>
      </div>
    )
  }

  return (
    <div className='d-flex flex-column gap-6 dashboard-page'>
      <div className='d-flex flex-wrap justify-content-between align-items-center gap-3'>
        <div>
          <h2 className='fs-2 fw-bold mb-1'>Overview{projectLabel ? ` - ${projectLabel}` : ''}</h2>
          <div className='text-gray-500'>Project snapshot of submitted and total variations.</div>
        </div>
      </div>

      <div className='row g-4'>
        <div className='col-12 col-md-6 col-xl-3'>
          <div className='card card-flush shadow-sm h-100 dashboard-summary-tile'>
            <div className='card-body d-flex flex-column justify-content-center'>
              <div className='dashboard-summary-tile__title'>Project Completion Date</div>
              <div className='dashboard-summary-tile__value'>
                {loadingVariations ? 'Loading...' : projectCompletionDateLabel}
              </div>
            </div>
          </div>
        </div>
        <div className='col-12 col-md-6 col-xl-3'>
          <div className='card card-flush shadow-sm h-100 dashboard-summary-tile'>
            <div className='card-body d-flex flex-column justify-content-center'>
              <div className='dashboard-summary-tile__title'>Contract Sum</div>
              <div className='dashboard-summary-tile__value'>
                {loadingVariations ? 'Loading...' : formatCurrency(budgetSummary.contractSum)}
              </div>
            </div>
          </div>
        </div>
        <div className='col-12 col-md-6 col-xl-3'>
          <div className='card card-flush shadow-sm h-100 dashboard-summary-tile'>
            <div className='card-body d-flex flex-column justify-content-center'>
              <div className='dashboard-summary-tile__title'>Adjusted Project Budget</div>
              <div className='dashboard-summary-tile__value'>
                {loadingVariations ? 'Loading...' : formatCurrency(budgetSummary.adjustedBudget)}
              </div>
            </div>
          </div>
        </div>
        <div className='col-12 col-md-6 col-xl-3'>
          <div className='card card-flush shadow-sm h-100 dashboard-summary-tile'>
            <div className='card-body d-flex flex-column justify-content-center'>
              <div className='dashboard-summary-tile__title'>Committed Amount</div>
              <div className='dashboard-summary-tile__value'>
                {loadingVariations ? 'Loading...' : formatCurrency(budgetSummary.committed)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='row g-6'>
        <div className='col-12 col-lg-6 col-xl-4'>
          <div className='card card-flush shadow-sm h-100 dashboard-tile'>
            <div className='card-body d-flex flex-column gap-4'>
              <div className='d-flex align-items-center gap-3'>
                <h3 className='mb-0 fs-4 fw-bold'>Submitted Variation Age</h3>
                <span className='badge dashboard-count-badge fw-semibold'>{submittedCount}</span>
              </div>
              <div className='text-gray-600'>
                {submittedCount > 0
                  ? `There are currently ${submittedCount} variations in "Submitted" status.`
                  : 'There are currently no variations in "Submitted" status.'}
              </div>
              {loadingVariations ? (
                <div className='dashboard-empty'>
                  <span className='spinner-border me-2'></span>
                  Loading submitted variations...
                </div>
              ) : submittedCount === 0 ? (
                <div className='dashboard-empty'>No submitted variations at this time.</div>
              ) : (
                <div className='dashboard-chart-scroll'>
                  <Chart
                    renderAs='canvas'
                    transitions={false}
                    style={{height: submittedChartHeight}}
                    onSeriesClick={handleSubmittedBarClick}
                  >
                    <ChartArea height={submittedChartHeight} margin={{right: 64}} />
                    <ChartCategoryAxis>
                      <ChartCategoryAxisItem labels={{font: '12px sans-serif'}} />
                    </ChartCategoryAxis>
                    <ChartValueAxis>
                      <ChartValueAxisItem
                        title={{text: 'Variation Age - Days'}}
                        min={0}
                        max={submittedValueAxisMax}
                      />
                    </ChartValueAxis>
                    <ChartSeries>
                      <ChartSeriesItem
                        type='bar'
                        data={submittedItems}
                        field='ageDays'
                        categoryField='variationNumber'
                        color={chartColors.primary}
                        gap={0.6}
                        spacing={0.12}
                        labels={{
                          visible: true,
                          content: (event) =>
                            formatCurrency((event.dataItem as SubmittedVariationItem).amount),
                        }}
                      />
                    </ChartSeries>
                  </Chart>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className='col-12 col-lg-6 col-xl-4'>
          <div className='card card-flush shadow-sm h-100 dashboard-tile'>
            <div className='card-body d-flex flex-column gap-4'>
              <div className='d-flex align-items-center gap-3'>
                <h3 className='mb-0 fs-4 fw-bold'>Variation Status</h3>
                <span className='badge dashboard-count-badge fw-semibold'>
                  {allVariationsCount}
                </span>
              </div>
              <div className='text-gray-600'>
                {projectLabel
                  ? `All variations for ${projectLabel}.`
                  : 'All variations for this project.'}
              </div>
              {loadingVariations ? (
                <div className='dashboard-empty'>
                  <span className='spinner-border me-2'></span>
                  Loading variations...
                </div>
              ) : allVariationsCount === 0 ? (
                <div className='dashboard-empty'>
                  No variations in configured statuses at this time.
                </div>
              ) : (
                <div
                  className={`dashboard-chart-scroll ${
                    allVariationsRequiresHorizontalScroll ? 'dashboard-chart-scroll--x' : ''
                  }`.trim()}
                >
                  <Chart
                    renderAs='canvas'
                    transitions={false}
                    style={{
                      width: allVariationsRequiresHorizontalScroll
                        ? allVariationsChartWidth
                        : '100%',
                      height: 280,
                    }}
                    onSeriesClick={(event) => handleAllVariationsBarClick(event.dataItem)}
                  >
                    <ChartArea height={230} />
                    <ChartCategoryAxis>
                      <ChartCategoryAxisItem
                        labels={{
                          rotation: 0,
                          padding: 8,
                          font: '11px sans-serif',
                          content: (event) => wrapCategoryAxisLabel(String(event.value ?? '')),
                        }}
                      />
                    </ChartCategoryAxis>
                    <ChartValueAxis>
                      <ChartValueAxisItem min={0} />
                    </ChartValueAxis>
                    <ChartSeries>
                      <ChartSeriesItem
                        type='column'
                        data={statusSummaries}
                        field='count'
                        categoryField='axisLabel'
                        colorField='color'
                        gap={0.75}
                        spacing={0.08}
                        labels={{
                          visible: true,
                          content: (event) =>
                            formatCurrency((event.dataItem as StatusSummaryItem).totalAmount),
                        }}
                      />
                    </ChartSeries>
                  </Chart>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className='col-12 col-lg-6 col-xl-4'>
          <div className='card card-flush shadow-sm h-100 dashboard-tile'>
            <div className='card-body d-flex flex-column gap-4'>
              <div className='d-flex align-items-center gap-3'>
                <h3 className='mb-0 fs-4 fw-bold'>Contingency Remaining</h3>
              </div>
              <div className='text-gray-600'>Contingency remaining for this project.</div>
              {loadingVariations ? (
                <div className='dashboard-empty'>
                  <span className='spinner-border me-2'></span>
                  Loading contingency...
                </div>
              ) : !contingencySummary.hasData ? (
                <div className='dashboard-empty'>No contingency data at this time.</div>
              ) : (
                <div className='dashboard-contingency'>
                  <div
                    className='contingency-gauge'
                    role='img'
                    aria-label={`${contingencyPercentLabel}% remaining`}
                  >
                    <svg viewBox='0 0 160 90' aria-hidden='true'>
                      <path
                        className='contingency-gauge__track'
                        d='M10 80 A70 70 0 0 1 150 80'
                        fill='none'
                        stroke='#e5e7eb'
                        strokeWidth='12'
                        strokeLinecap='round'
                      />
                      <path
                        className='contingency-gauge__value'
                        d='M10 80 A70 70 0 0 1 150 80'
                        fill='none'
                        stroke={brandOrange}
                        strokeWidth='12'
                        strokeLinecap='round'
                        strokeDasharray={`${gaugeDash} ${gaugeGap}`}
                      />
                    </svg>
                    <div className='contingency-gauge__label'>
                      <div className='contingency-gauge__percent'>{contingencyPercentLabel}%</div>
                      <div className='contingency-gauge__text'>Remaining</div>
                    </div>
                  </div>
                  <div className='d-flex flex-column gap-2'>
                    <div className='d-flex justify-content-between'>
                      <span className='text-gray-600'>Total Contingency:</span>
                      <span className='fw-semibold'>
                        {formatCurrency(contingencySummary.total)}
                      </span>
                    </div>
                    <div className='d-flex justify-content-between'>
                      <span className='text-gray-600'>Committed:</span>
                      <span className='fw-semibold'>
                        {formatCurrency(contingencySummary.committed)}
                      </span>
                    </div>
                    <div className='d-flex justify-content-between'>
                      <span className='fw-semibold text-gray-800'>Remaining:</span>
                      <span className='fw-semibold text-warning'>
                        {formatCurrency(contingencySummary.remaining)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className='row g-6' ref={pcgChartsSectionRef}>
        <div className='col-12 col-xl-6'>
          <div className='card card-flush shadow-sm h-100 dashboard-tile dashboard-chart-tile'>
            <div className='card-body d-flex flex-column gap-4'>
              <div>
                <h3 className='mb-1 fs-3 fw-bold'>Construction Cashflow</h3>
                <div className='text-gray-600'>Actual vs forecast monthly cash flow.</div>
              </div>
              {pcgChartsLoading ? (
                <div className='dashboard-empty'>
                  <span className='spinner-border me-2'></span>
                  Loading PCG charts...
                </div>
              ) : !hasPcgReport ? (
                <div className='dashboard-empty'>No PCG report available.</div>
              ) : !hasConstructionChartData ? (
                <div className='dashboard-empty'>No construction cashflow data available.</div>
              ) : (
                <Chart renderAs='canvas' transitions={false}>
                  <ChartArea height={320} />
                  <ChartCategoryAxis>
                    <ChartCategoryAxisItem
                      categories={constructionMonthlyChartData.categories}
                      labels={{rotation: -35, padding: 4}}
                    />
                  </ChartCategoryAxis>
                  <ChartValueAxis>
                    <ChartValueAxisItem labels={{format: '$#,0'}} />
                  </ChartValueAxis>
                  <ChartSeries>
                    <ChartSeriesItem
                      type='column'
                      data={constructionMonthlyChartData.actualMonthly}
                      name='Actual Monthly Cash Flow'
                      color={chartColors.primary}
                      gap={2}
                      spacing={0.3}
                    />
                    <ChartSeriesItem
                      type='column'
                      data={constructionMonthlyChartData.forecastMonthly}
                      name='Forecast Monthly Cash Flow'
                      color={chartColors.info}
                      gap={2}
                      spacing={0.3}
                    />
                  </ChartSeries>
                  <ChartLegend position='bottom' orientation='horizontal' />
                </Chart>
              )}
            </div>
          </div>
        </div>

        <div className='col-12 col-xl-6'>
          <div className='card card-flush shadow-sm h-100 dashboard-tile dashboard-chart-tile'>
            <div className='card-body d-flex flex-column gap-4'>
              <div>
                <h3 className='mb-1 fs-3 fw-bold'>Cumulative Construction Cashflow</h3>
                <div className='text-gray-600'>
                  Actual cumulative expenditure against forecast cumulative cash flow.
                </div>
              </div>
              {pcgChartsLoading ? (
                <div className='dashboard-empty'>
                  <span className='spinner-border me-2'></span>
                  Loading PCG charts...
                </div>
              ) : !hasPcgReport ? (
                <div className='dashboard-empty'>No PCG report available.</div>
              ) : !hasConstructionChartData ? (
                <div className='dashboard-empty'>No construction cashflow data available.</div>
              ) : (
                <Chart renderAs='canvas' transitions={false}>
                  <ChartArea height={320} />
                  <ChartCategoryAxis>
                    <ChartCategoryAxisItem
                      categories={constructionMonthlyChartData.categories}
                      labels={{rotation: -35, padding: 4}}
                    />
                  </ChartCategoryAxis>
                  <ChartValueAxis>
                    <ChartValueAxisItem labels={{format: '$#,0'}} />
                  </ChartValueAxis>
                  <ChartSeries>
                    <ChartSeriesItem
                      type='line'
                      data={constructionMonthlyChartData.actualCumulative}
                      name='Actual Cumulative Expenditure'
                      color={chartColors.primary}
                      style='smooth'
                      markers={{visible: true, size: 4}}
                      missingValues='gap'
                      zIndex={2}
                    />
                    <ChartSeriesItem
                      type='line'
                      data={constructionMonthlyChartData.forecastCumulative}
                      name='Forecast Cumulative Cash Flow'
                      color={chartColors.info}
                      style='smooth'
                      markers={{visible: true, size: 4}}
                      zIndex={1}
                    />
                  </ChartSeries>
                  <ChartLegend position='bottom' orientation='horizontal' />
                </Chart>
              )}
            </div>
          </div>
        </div>

        <div className='col-12 col-xl-6'>
          <div className='card card-flush shadow-sm h-100 dashboard-tile dashboard-chart-tile'>
            <div className='card-body d-flex flex-column gap-4'>
              <div>
                <h3 className='mb-1 fs-3 fw-bold'>Contingency Erosion</h3>
                <div className='text-gray-600'>Expenditure vs contingency (monthly).</div>
              </div>
              {pcgChartsLoading ? (
                <div className='dashboard-empty'>
                  <span className='spinner-border me-2'></span>
                  Loading PCG charts...
                </div>
              ) : !hasPcgReport ? (
                <div className='dashboard-empty'>No PCG report available.</div>
              ) : !hasContingencyChartData ? (
                <div className='dashboard-empty'>No contingency data available.</div>
              ) : (
                <Chart renderAs='canvas' transitions={false}>
                  <ChartArea height={320} />
                  <ChartCategoryAxis>
                    <ChartCategoryAxisItem
                      categories={contingencyChartCategories}
                      labels={{rotation: -45, padding: 4}}
                    />
                  </ChartCategoryAxis>
                  <ChartValueAxis>
                    <ChartValueAxisItem
                      min={0}
                      max={100}
                      majorUnit={10}
                      labels={{format: '{0}%'}}
                    />
                  </ChartValueAxis>
                  <ChartSeries>
                    {contingencyErosionData?.expenditureVsContingencySeries.map((entry) => (
                      <ChartSeriesItem
                        key={entry.key}
                        type='line'
                        data={entry.values}
                        name={entry.label}
                        color={chartColors.contingencySeries[entry.key]}
                        style='smooth'
                        missingValues='gap'
                        markers={{visible: true, size: 4}}
                      />
                    ))}
                  </ChartSeries>
                  <ChartLegend position='bottom' orientation='horizontal' />
                </Chart>
              )}
            </div>
          </div>
        </div>

        <div className='col-12 col-xl-6'>
          <div className='card card-flush shadow-sm h-100 dashboard-tile dashboard-chart-tile'>
            <div className='card-body d-flex flex-column gap-4'>
              <div>
                <h3 className='mb-1 fs-3 fw-bold'>Contingency Erosion</h3>
                <div className='text-gray-600'>
                  Cumulative cash flow against cumulative expenditure.
                </div>
              </div>
              {pcgChartsLoading ? (
                <div className='dashboard-empty'>
                  <span className='spinner-border me-2'></span>
                  Loading PCG charts...
                </div>
              ) : !hasPcgReport ? (
                <div className='dashboard-empty'>No PCG report available.</div>
              ) : !hasContingencyChartData ? (
                <div className='dashboard-empty'>No contingency data available.</div>
              ) : (
                <Chart renderAs='canvas' transitions={false}>
                  <ChartArea height={320} />
                  <ChartCategoryAxis>
                    <ChartCategoryAxisItem
                      categories={contingencyChartCategories}
                      labels={{rotation: -45, padding: 4}}
                    />
                  </ChartCategoryAxis>
                  <ChartValueAxis>
                    <ChartValueAxisItem labels={{format: '$#,0'}} />
                  </ChartValueAxis>
                  <ChartSeries>
                    {contingencyErosionData?.contingencySeries.map((entry) => (
                      <ChartSeriesItem
                        key={entry.key}
                        type='line'
                        data={entry.values}
                        name={entry.label}
                        color={chartColors.contingencySeries[entry.key]}
                        style='smooth'
                        missingValues='gap'
                        markers={{visible: true, size: 4}}
                      />
                    ))}
                  </ChartSeries>
                  <ChartLegend position='bottom' orientation='horizontal' />
                </Chart>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const DashboardWrapper: FC = () => {
  const intl = useIntl()

  return (
    <>
      <PageTitle breadcrumbs={[]}>{intl.formatMessage({id: 'MENU.DASHBOARD'})}</PageTitle>
      <DashboardPage />
    </>
  )
}

export {DashboardWrapper}
